import logger from "@/lib/logger";
import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { ClipboardList, Search, PlusCircle, Eye, Printer, ChevronLeft, ChevronRight, X, AlertCircle, Download, Loader2, MapPin, Trash2, ChevronDown, Wrench, StickyNote, Clock } from "lucide-react";
import { StatusBadge, PIPELINE, LEGACY_PIPELINE } from "@/components/StatusPipeline";
import { INDIA_STATES } from "@/lib/constants";
import api from "@/lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAY_STYLES = {
  "Unpaid":  "bg-rose-50 text-rose-700 border border-rose-200",
  "Partial": "bg-amber-50 text-amber-700 border border-amber-200",
  "Paid":    "bg-emerald-50 text-emerald-700 border border-emerald-200",
};
const PayBadge = ({ s }) => <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${PAY_STYLES[s]||"bg-slate-100"}`} data-testid="payment-status-chip">{s}</span>;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

export default function JobsList() {
  const navigate = useNavigate();
  const [jobs,    setJobs]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [pages,   setPages]   = useState(1);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [page,    setPage]    = useState(1);
  const [search,  setSearch]  = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [payFilter,    setPayFilter]    = useState("all");
  const [cityFilter,   setCityFilter]   = useState("");
  const [stateFilter,  setStateFilter]  = useState("all");
  const [techFilter,   setTechFilter]   = useState("all");
  const [technicians,  setTechnicians]  = useState([]);
  const [overdueIds,   setOverdueIds]   = useState(new Set());

  // Selection for bulk print
  const [selected,     setSelected]     = useState(new Set());
  const [showDelConfirm, setShowDelConfirm] = useState(false);
  const [deleting,     setDeleting]     = useState(false);
  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => {
    if (selected.size === jobs.length) setSelected(new Set());
    else setSelected(new Set(jobs.map(j => j.id)));
  };
  const openBulkPrint = () => {
    const ids = [...selected].join(",");
    window.open(`/bulk-print?jobs=${ids}`, "_blank");
  };

  const deleteSelected = async () => {
    setDeleting(true);
    try {
      const res = await api.delete(`${API}/jobs/bulk`, { data: { ids: [...selected] } });
      toast.success(`Deleted ${res.data.deleted} job${res.data.deleted !== 1 ? "s" : ""}`);
      setSelected(new Set()); setShowDelConfirm(false); fetchJobs();
    } catch (err) {
      logger.error("[JobsList] deleteSelected:", err?.message);
      toast.error("Failed to delete jobs");
    } finally { setDeleting(false); }
  };

  // ── Quick status update (inline on row) ───────────────────────────────────
  const [quickStatusJobId,  setQuickStatusJobId]  = useState(null);
  const [quickStatusSaving, setQuickStatusSaving] = useState(null);
  const quickStatusRef = useRef(null);

  useEffect(() => {
    const close = e => {
      if (quickStatusRef.current && !quickStatusRef.current.contains(e.target))
        setQuickStatusJobId(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handleQuickStatus = async (e, job, newStatus) => {
    e.preventDefault();
    e.stopPropagation();
    if (newStatus === job.current_status) { setQuickStatusJobId(null); return; }
    setQuickStatusSaving(job.id);
    setQuickStatusJobId(null);
    try {
      await api.put(`${API}/jobs/${job.id}/status`, { status: newStatus, notes: "" });
      setJobs(prev => prev.map(j => j.id === job.id ? { ...j, current_status: newStatus } : j));
      toast.success(`${job.job_no} → ${newStatus}`);
    } catch (err) {
      logger.error("[JobsList] quickStatus:", err?.message);
      toast.error("Failed to update status");
    } finally { setQuickStatusSaving(null); }
  };

  // Deduplicated pipeline for dropdown (handles duplicate "In Transit")
  const PIPELINE_OPTS = LEGACY_PIPELINE.filter((s, i, arr) => arr.findIndex(x => x.status === s.status) === i);

  // ── Notes panel ───────────────────────────────────────────────────────────
  const [notesJob,    setNotesJob]    = useState(null);  // job object for notes panel
  const [noteText,    setNoteText]    = useState("");
  const [notesSaving, setNotesSaving] = useState(false);

  const openNotes = (e, job) => {
    e.preventDefault();
    e.stopPropagation();
    setNotesJob(job);
    setNoteText("");
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !notesJob) return;
    setNotesSaving(true);
    try {
      const r = await api.post(`${API}/jobs/${notesJob.id}/notes`, { text: noteText.trim() });
      const updated = r.data;
      setNotesJob(updated);
      setNoteText("");
      setJobs(prev => prev.map(j => j.id === updated.id ? { ...j, internal_notes: updated.internal_notes } : j));
      toast.success("Note saved");
    } catch (err) {
      logger.error("[JobsList] addNote:", err?.message);
      toast.error("Failed to save note");
    } finally { setNotesSaving(false); }
  };

  const handleDeleteNote = async (noteId) => {
    if (!notesJob) return;
    try {
      await api.delete(`${API}/jobs/${notesJob.id}/notes/${noteId}`);
      const updated = { ...notesJob, internal_notes: (notesJob.internal_notes || []).filter(n => n.id !== noteId) };
      setNotesJob(updated);
      setJobs(prev => prev.map(j => j.id === updated.id ? { ...j, internal_notes: updated.internal_notes } : j));
    } catch { toast.error("Failed to delete note"); }
  };

  const fmtDT = d => d ? new Date(d).toLocaleString("en-IN", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "";

  // CSV Export
  const now = new Date();
  const [showExport,   setShowExport]   = useState(false);
  const [exportYear,   setExportYear]   = useState(now.getFullYear());
  const [exportMonth,  setExportMonth]  = useState(now.getMonth() + 1);
  const [exporting,    setExporting]    = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const params = { page, limit: 20 };
      if (search)                    params.search         = search;
      if (statusFilter !== "all")    params.status         = statusFilter;
      if (payFilter    !== "all")    params.payment_status = payFilter;
      if (cityFilter.trim())         params.city           = cityFilter.trim();
      if (stateFilter  !== "all")    params.state          = stateFilter;
      if (techFilter   !== "all")    params.technician     = techFilter;
      const res = await api.get(`/jobs`, { params });
      setJobs(res.data.jobs || []); setTotal(res.data.total || 0); setPages(res.data.pages || 1);
      setSelected(new Set());
    } catch (err) {
      logger.error("[JobsList] fetchJobs failed:", err?.message);
      setError("Failed to load jobs");
    } finally { setLoading(false); }
  }, [page, search, statusFilter, payFilter, cityFilter, stateFilter, techFilter]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);
  useEffect(() => {
    // Load technicians + overdue jobs — api is a stable import, safe to omit from deps
    Promise.all([
      api.get(`/technicians`).catch(() => ({ data: { technicians: [] } })),
      api.get(`/alerts/overdue`).catch(() => ({ data: { overdue: [] } })),
    ]).then(([techRes, overdueRes]) => {
      setTechnicians(techRes.data.technicians || []);
      setOverdueIds(new Set((overdueRes.data.overdue || []).map(j => j.id)));
    }).catch(err => logger.error("[JobsList] init fetch failed:", err?.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => { e.preventDefault(); setSearch(searchInput); setPage(1); };
  const clearFilters = () => {
    setSearch(""); setSearchInput(""); setStatusFilter("all");
    setPayFilter("all"); setCityFilter(""); setStateFilter("all"); setTechFilter("all"); setPage(1);
  };
  const hasFilters = search || statusFilter !== "all" || payFilter !== "all" || cityFilter || stateFilter !== "all" || techFilter !== "all";

  // ── Live search: debounce 300ms so results update as the admin types ──────
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1);
      }
    }, 300);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const downloadCSV = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/reports/monthly?year=${exportYear}&month=${exportMonth}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement("a");
      a.href     = url;
      a.download = `XSTRONG_Jobs_${exportYear}_${String(exportMonth).padStart(2,"0")}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported!");
      setShowExport(false);
    } catch { toast.error("Failed to export CSV"); }
    finally  { setExporting(false); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Service Jobs</h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} total job{total !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && (
            <>
              <Button variant="outline" size="sm" className="h-8 text-xs border-green-200 text-green-700 hover:bg-green-50"
                onClick={openBulkPrint} data-testid="jobs-bulk-print-button">
                <Printer size={13} className="mr-1.5"/> Print Selected ({selected.size})
              </Button>
              <Button variant="outline" size="sm" className="h-8 text-xs border-rose-200 text-rose-600 hover:bg-rose-50"
                onClick={() => setShowDelConfirm(true)} data-testid="jobs-bulk-delete-button">
                <Trash2 size={13} className="mr-1.5"/> Delete Selected ({selected.size})
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" className="h-8 text-xs border-green-200 text-green-700 hover:bg-green-50" onClick={() => setShowExport(true)} data-testid="jobs-export-csv-button">
            <Download size={13} className="mr-1.5" /> Export CSV
          </Button>
          <Link to="/jobs/new">
            <Button className="bg-green-600 hover:bg-green-700 text-white" data-testid="jobs-new-job-button">
              <PlusCircle className="w-4 h-4 mr-2" /> New Job
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters row 1: search + status + payment */}
      <div className="flex flex-col gap-2 mb-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <form onSubmit={handleSearch} className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by job #, customer, phone, serial..." value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="pl-9 pr-4 h-9 text-sm bg-white border-slate-200" data-testid="jobs-search-input" />
          </form>
          <div className="flex gap-2 flex-wrap">
            <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger className="w-44 h-9 text-sm bg-white" data-testid="jobs-status-filter-select">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="all">All Status</SelectItem>
                {PIPELINE.map(p => (
                  <SelectItem key={p.status} value={p.status}>
                    <span className="flex items-center gap-2"><span>{p.emoji}</span><span>{p.status}</span></span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={payFilter} onValueChange={v => { setPayFilter(v); setPage(1); }}>
              <SelectTrigger className="w-36 h-9 text-sm bg-white">
                <SelectValue placeholder="All Payment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payment</SelectItem>
                <SelectItem value="Unpaid">Unpaid</SelectItem>
                <SelectItem value="Partial">Partial</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Filters row 2: city + state */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex-1 relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Filter by city (e.g. Mumbai, Delhi...)"
              value={cityFilter}
              onChange={e => { setCityFilter(e.target.value); setPage(1); }}
              className="pl-9 pr-4 h-9 text-sm bg-white border-slate-200"
              data-testid="jobs-city-filter-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Select value={stateFilter} onValueChange={v => { setStateFilter(v); setPage(1); }}>
              <SelectTrigger className="w-48 h-9 text-sm bg-white" data-testid="jobs-state-filter-select">
                <SelectValue placeholder="All States" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="all">All States</SelectItem>
                {INDIA_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {technicians.length > 0 && (
              <Select value={techFilter} onValueChange={v => { setTechFilter(v); setPage(1); }}>
                <SelectTrigger className="w-44 h-9 text-sm bg-white" data-testid="jobs-tech-filter-select">
                  <SelectValue placeholder="All Technicians" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Technicians</SelectItem>
                  {technicians.map(t => <SelectItem key={t} value={t}>👷 {t}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {hasFilters && (
              <Button variant="outline" size="sm" onClick={clearFilters} className="h-9 text-xs" data-testid="jobs-clear-filters-button">
                <X className="w-3.5 h-3.5 mr-1" /> Clear All
              </Button>
            )}
          </div>
        </div>

        {/* Active filter chips */}
        {(cityFilter || stateFilter !== "all") && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-400 flex items-center gap-1"><MapPin size={11}/> Area filters:</span>
            {cityFilter && (
              <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 px-2.5 py-0.5 rounded-full font-medium">
                📍 {cityFilter}
                <button onClick={() => { setCityFilter(""); setPage(1); }} className="hover:text-green-900 ml-0.5">×</button>
              </span>
            )}
            {stateFilter !== "all" && (
              <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 px-2.5 py-0.5 rounded-full font-medium">
                🗺️ {stateFilter}
                <button onClick={() => { setStateFilter("all"); setPage(1); }} className="hover:text-green-900 ml-0.5">×</button>
              </span>
            )}
          </div>
        )}
      </div>

      {error && <div className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl mb-4 text-sm text-rose-700"><AlertCircle size={15}/>{error}</div>}

      <Card className="rounded-2xl border border-slate-200/70 bg-white overflow-hidden" data-testid="jobs-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    checked={jobs.length > 0 && selected.size === jobs.length}
                    onCheckedChange={toggleAll}
                    data-testid="jobs-select-all-checkbox"
                  />
                </th>
                {["Job #","Customer","Product / Model","Status","Payment","Amount","Date","Actions"].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wide px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? ([...Array(5)].map((_, i) => (
                <tr key={i} className="border-b border-slate-50">{[...Array(9)].map((_,j) => <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-full" /></td>)}</tr>
              ))) : jobs.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center"><ClipboardList className="w-7 h-7 text-slate-400" /></div>
                    <div><p className="text-sm font-medium text-slate-700">No jobs found</p><p className="text-xs text-slate-400 mt-0.5">{hasFilters ? "Try adjusting filters" : "Create first service job"}</p></div>
                    {!hasFilters && <Link to="/jobs/new"><Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">New Job</Button></Link>}
                  </div>
                </td></tr>
              ) : jobs.map(job => (
                <tr key={job.id}
                  className={`border-b border-slate-50 hover:bg-slate-50/60 transition-colors cursor-pointer ${selected.has(job.id) ? "bg-green-50/40" : ""}`}
                  onClick={() => navigate(`/jobs/${job.id}`)}>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(job.id)}
                      onCheckedChange={() => toggleSelect(job.id)}
                      data-testid="jobs-row-checkbox"
                    />
                  </td>
                  <td className="px-4 py-3"><span className="font-mono text-sm font-semibold text-green-700">{job.job_no}</span></td>
                  <td className="px-4 py-3"><p className="text-sm font-medium text-slate-900">{job.customer?.name}</p>
                    <p className="text-xs text-slate-500">{job.customer?.phone}</p>
                    {job.customer?.city && <p className="text-xs text-slate-400 flex items-center gap-0.5 mt-0.5"><MapPin size={10}/>{job.customer.city}{job.customer?.state ? `, ${job.customer.state}` : ""}</p>}
                    {(job.internal_notes || []).length > 0 && (
                      <p className="text-xs text-slate-400 italic flex items-center gap-1 mt-1 max-w-[180px]">
                        <StickyNote size={10} className="flex-shrink-0 text-green-500" />
                        <span className="truncate">{job.internal_notes[job.internal_notes.length - 1].text}</span>
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs text-slate-600">{(job.products||[]).length > 0 ? `${(job.products||[]).length} charger${(job.products||[]).length!==1?"s":""}` : "—"}</p>
                    <p className="text-xs font-mono text-green-700">{(job.products||[])[0]?.charger_spec || "—"}</p>
                    {(job.products||[]).some(p => p.xp_charger_status) && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-200 px-1.5 py-0.5 rounded-full mt-0.5">
                        🔄 XP
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex flex-col gap-0.5">
                      {/* Quick status dropdown */}
                      <div className="relative" ref={quickStatusJobId === job.id ? quickStatusRef : null}>
                        <button
                          onClick={e => { e.stopPropagation(); setQuickStatusJobId(prev => prev === job.id ? null : job.id); }}
                          className="flex items-center gap-1 group focus:outline-none"
                          title="Tap to change status"
                          data-testid={`jobs-quick-status-${job.job_no}`}>
                          {quickStatusSaving === job.id
                            ? <span className="inline-flex items-center gap-1 text-xs px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                                <Loader2 size={10} className="animate-spin" /> Saving…
                              </span>
                            : <>
                                <StatusBadge status={job.current_status} />
                                <ChevronDown size={11} className="text-slate-400 group-hover:text-slate-600 -ml-1 transition-colors" />
                              </>}
                        </button>

                        {quickStatusJobId === job.id && (
                          <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden min-w-[200px]">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide px-3 pt-2.5 pb-1">
                              Update Status
                            </p>
                            {PIPELINE_OPTS.map(step => {
                              const isCurrent = step.status === job.current_status;
                              return (
                                <button
                                  key={step.status}
                                  onClick={e => handleQuickStatus(e, job, step.status)}
                                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left transition-colors ${
                                    isCurrent
                                      ? "bg-green-50 text-green-800 font-semibold"
                                      : "text-slate-700 hover:bg-slate-50"
                                  }`}>
                                  <span className="text-sm">{step.emoji}</span>
                                  <span>{step.status}</span>
                                  {isCurrent && <span className="ml-auto text-[9px] bg-green-200 text-green-800 px-1.5 py-0.5 rounded-full font-bold">Current</span>}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {overdueIds.has(job.id) && (
                        <span className="inline-flex items-center gap-0.5 text-xs bg-amber-100 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded-full font-semibold" title="Overdue — in repair >3 days">
                          ⚠️ Overdue
                        </span>
                      )}
                      {job.technician && job.technician !== "unassigned" && (
                        <p className="text-xs text-purple-600 mt-0.5 flex items-center gap-0.5">👷 {job.technician}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3"><PayBadge s={job.payment?.payment_status} /></td>
                  <td className="px-4 py-3"><p className="text-sm tabular-nums font-medium text-slate-900">₹{(job.payment?.amount||0).toLocaleString("en-IN")}</p>{job.payment?.due_amount>0&&<p className="text-xs text-rose-600">Due: ₹{job.payment.due_amount.toLocaleString("en-IN")}</p>}</td>
                  <td className="px-4 py-3"><p className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(job.created_at)}</p></td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1 flex-wrap">
                      <Link to={`/jobs/${job.id}`}><Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-green-600" title="View" data-testid="jobs-row-open-button"><Eye size={14}/></Button></Link>
                      <a href={`/print/${job.id}`} target="_blank" rel="noreferrer"><Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-green-600" title="Print" data-testid="jobs-row-print-button"><Printer size={14}/></Button></a>
                      {/* Notes icon — filled green if job has notes */}
                      {(() => {
                        const noteCount = (job.internal_notes || []).length;
                        const hasNotes  = noteCount > 0;
                        return (
                          <div className="relative">
                            <Button variant="ghost" size="icon"
                              className={`h-7 w-7 transition-colors ${hasNotes ? "text-green-600 bg-green-50 hover:bg-green-100" : "text-slate-400 hover:text-green-600 hover:bg-green-50"}`}
                              title={hasNotes ? `${noteCount} note${noteCount !== 1 ? "s" : ""}` : "Add note"}
                              onClick={e => openNotes(e, job)}
                              data-testid={`jobs-notes-btn-${job.job_no}`}>
                              <StickyNote size={14} />
                            </Button>
                            {hasNotes && (
                              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-green-600 text-white text-[8px] font-bold rounded-full flex items-center justify-center leading-none pointer-events-none">
                                {noteCount > 9 ? "9+" : noteCount}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                      {job.source === "customer_portal" && (
                        <Link
                          to="/sc/new"
                          state={{ prefill: {
                            customer: {
                              name:      job.customer?.name     || "",
                              phone:     job.customer?.phone    || "",
                              alt_phone: job.customer?.alt_phone|| "",
                              address:   job.customer?.address  || "",
                              landmark:  job.customer?.landmark || "",
                              city:      job.customer?.city     || "",
                              state:     job.customer?.state    || "",
                              pincode:   job.customer?.pincode  || "",
                            },
                            chargers: (job.products || []).map(p => ({
                              charger_type:    p.charger_type    || "",
                              charger_spec:    p.charger_spec    || p.charger_spec || "",
                              serial_number:   p.serial_number   || "",
                              body_type:       p.body_type       || "",
                              warranty_status: "",
                              mfg_month:       (p.purchase_date || "").split(" ")[0]
                                ? String(["January","February","March","April","May","June","July","August","September","October","November","December"].indexOf((p.purchase_date||"").split(" ")[0]) + 1)
                                : "",
                              mfg_year:        (p.purchase_date || "").split(" ")[1] || "",
                            })),
                            from_job_no: job.job_no,
                          }}}
                          title={`Create SC Job from ${job.job_no}`}
                          data-testid={`jobs-row-create-sc-${job.job_no}`}>
                          <Button variant="ghost" size="sm"
                            className="h-7 px-2 text-[10px] font-semibold text-green-700 hover:text-green-800 hover:bg-green-50 border border-green-200 rounded-lg gap-1">
                            <Wrench size={11} /> SC Job
                          </Button>
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <p className="text-xs text-slate-500">Showing {(page-1)*20+1}\u2013{Math.min(page*20,total)} of {total}</p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-7 w-7" disabled={page===1} onClick={() => setPage(p=>p-1)}><ChevronLeft size={13}/></Button>
              <span className="text-xs text-slate-600 px-2">Page {page} of {pages}</span>
              <Button variant="outline" size="icon" className="h-7 w-7" disabled={page===pages} onClick={() => setPage(p=>p+1)}><ChevronRight size={13}/></Button>
            </div>
          </div>
        )}
      </Card>

      {/* Export CSV Dialog */}
      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Export Monthly Report</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium text-slate-700">Year</Label>
              <Input type="number" min="2020" max="2099" value={exportYear}
                onChange={e => setExportYear(parseInt(e.target.value)||now.getFullYear())}
                className="mt-1.5 h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700">Month</Label>
              <Select value={String(exportMonth)} onValueChange={v => setExportMonth(parseInt(v))}>
                <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500 bg-slate-50 p-2.5 rounded-lg">
              Downloads: XSTRONG_Jobs_{exportYear}_{String(exportMonth).padStart(2,"0")}.csv
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowExport(false)}>Cancel</Button>
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={downloadCSV} disabled={exporting}>
              {exporting ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Download size={13} className="mr-1.5" />} Download CSV
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={showDelConfirm} onOpenChange={setShowDelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} job{selected.size !== 1 ? "s" : ""}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{selected.size}</strong> selected job{selected.size !== 1 ? "s" : ""}.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              onClick={deleteSelected}
              disabled={deleting}
              data-testid="jobs-bulk-delete-confirm"
            >
              {deleting ? <><Loader2 size={13} className="mr-1.5 animate-spin" />Deleting...</> : `Delete ${selected.size} Job${selected.size !== 1 ? "s" : ""}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Notes Panel ──────────────────────────────────────────────────── */}
      <Sheet open={!!notesJob} onOpenChange={open => { if (!open) { setNotesJob(null); setNoteText(""); } }}>
        <SheetContent side="right" className="w-full sm:w-[420px] flex flex-col p-0">
          <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <StickyNote size={15} className="text-green-600" />
              <SheetTitle className="text-base" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                Notes — {notesJob?.job_no}
              </SheetTitle>
            </div>
            <SheetDescription className="text-xs text-slate-400">
              Internal only — not visible to customers · {notesJob?.customer?.name}
            </SheetDescription>
          </SheetHeader>

          {/* Notes list */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {(notesJob?.internal_notes || []).length === 0 ? (
              <div className="flex flex-col items-center py-12 text-center">
                <StickyNote size={28} className="text-slate-200 mb-3" />
                <p className="text-sm text-slate-400 font-medium">No notes yet</p>
                <p className="text-xs text-slate-300 mt-1">Add a note below</p>
              </div>
            ) : (
              [...(notesJob?.internal_notes || [])].reverse().map(note => (
                <div key={note.id} className="bg-slate-50 rounded-xl p-3.5 border border-slate-100 group">
                  <p className="text-sm text-slate-800 leading-relaxed">{note.text}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock size={9} /> {fmtDT(note.created_at)}
                    </p>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-slate-300 hover:text-rose-500 rounded transition-all">
                      <X size={12} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Add note input */}
          <div className="px-5 py-4 border-t border-slate-100 bg-white">
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Add New Note</Label>
            <Textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Type your note here…"
              className="text-sm resize-none mb-2"
              rows={3}
              onKeyDown={e => { if (e.key === "Enter" && e.ctrlKey) handleAddNote(); }}
            />
            <Button
              className="w-full bg-green-600 hover:bg-green-700 text-white text-sm h-9"
              disabled={!noteText.trim() || notesSaving}
              onClick={handleAddNote}
              data-testid="jobs-save-note-btn">
              {notesSaving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Save Note
            </Button>
            <p className="text-[10px] text-slate-400 text-center mt-1.5">Ctrl + Enter to save</p>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
